import React, { useRef } from 'react';
import styled from 'styled-components';

import { TrendChart, VerticalColumnChart } from 'components/Intelligence';
import useMeasuredWidth from 'components/Intelligence/useMeasuredWidth';
import { IntelligenceThemeScope } from 'containers/Intelligence/theme';

import { seriesColor } from '../../chartAdapters';
import OrbitChart from '../../components/OrbitChart';
import { orbitType } from '../../styles';
import { SupportChart as SupportChartModel, SupportValueFormat } from '../types';
import { CompactTable, sp, VisuallyHidden } from './supportStyles';

const FALLBACK_WIDTH = 360;
const PLOT_HEIGHT = 200;

export const formatSupportValue = (
  value: number | null | undefined,
  format: SupportValueFormat,
  currency?: string | null
): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '—';
  }
  const grouped = value.toLocaleString('en-US', { maximumFractionDigits: 2 });
  if (format === 'percent') {
    return `${grouped}%`;
  }
  if (format === 'currency') {
    return currency ? `${currency} ${grouped}` : grouped;
  }
  return grouped;
};

/** True when the chart carries at least one category and one series with a reading. */
export const hasChartData = (chart: SupportChartModel): boolean =>
  Array.isArray(chart.categories) &&
  chart.categories.length > 0 &&
  Array.isArray(chart.series) &&
  chart.series.some(series => series.values.some(value => value !== null));

const Slot = styled.div`
  width: 100%;
  min-width: 0;
`;

const Caption = styled.p`
  margin: 0 0 6px;
  color: ${sp.muted};
  ${orbitType.helping}
`;

/** The unknown-kind fallback: the chart's data as a compact table. */
export const SupportChartTable: React.FC<{ chart: SupportChartModel; title?: string | null }> = ({
  chart,
  title
}) => (
  <CompactTable data-testid="orbit-support-chart-table">
    <VisuallyHidden>{title ?? 'Chart data'}</VisuallyHidden>
    <thead>
      <tr>
        <th aria-label="Category" />
        {chart.series.map(series => (
          <th key={series.name}>{series.name}</th>
        ))}
      </tr>
    </thead>
    <tbody>
      {chart.categories.map((category, index) => (
        <tr key={category}>
          <td>{category}</td>
          {chart.series.map(series => (
            <td key={series.name}>
              {formatSupportValue(series.values[index], chart.format, chart.currency)}
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  </CompactTable>
);

/**
 * One chart block, any kind, drawn with the components the Intelligence screens and the Orbit
 * answer already use: the Orbit donut ring for donut/pie, the Intelligence `TrendChart` for
 * line/area and `VerticalColumnChart` for bars. `stacked_bar` is drawn GROUPED: no shared chart
 * component stacks bars.
 */
const SupportChart: React.FC<{ chart: SupportChartModel; title?: string | null }> = ({
  chart,
  title
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const measured = useMeasuredWidth(ref);
  const width = measured > 0 ? measured : FALLBACK_WIDTH;
  const format = (value: number) => formatSupportValue(value, chart.format, chart.currency);

  const render = (): React.ReactNode => {
    switch (chart.kind) {
      case 'donut':
      case 'pie': {
        const [series] = chart.series;
        return (
          <OrbitChart
            interactive={false}
            model={{
              kind: 'donut',
              title: title ?? '',
              centerValue: chart.total?.value ?? '',
              points: [],
              format: chart.format,
              currency: chart.currency ?? undefined,
              segments: chart.categories.map((label, index) => ({
                label,
                value: series.values[index],
                display: format(series.values[index] ?? Number.NaN),
                color: seriesColor(index)
              }))
            }}
          />
        );
      }
      case 'line':
      case 'area':
        return (
          <IntelligenceThemeScope>
            <div data-testid={`orbit-support-chart-${chart.kind}`}>
              <TrendChart
                series={chart.series.map((series, index) => ({
                  name: series.name,
                  color: seriesColor(index),
                  points: series.values.map(value => (value === null ? Number.NaN : value))
                }))}
                pointLabels={chart.categories}
                variant={chart.kind}
                width={width}
                height={PLOT_HEIGHT}
                showAxes
                showLegend={chart.legend ?? chart.series.length > 1}
                formatValue={format}
                formatAxisValue={format}
                ariaLabel={title ?? undefined}
              />
            </div>
          </IntelligenceThemeScope>
        );
      case 'bar':
      case 'stacked_bar': {
        const grouped = chart.series.length > 1;
        return (
          <IntelligenceThemeScope>
            <div data-testid="orbit-support-chart-bar">
              <VerticalColumnChart
                columns={chart.categories.map((label, index) => {
                  const values = chart.series.map(series => series.values[index] ?? 0);
                  return {
                    label,
                    value: values.reduce((sum, value) => sum + value, 0),
                    values: grouped ? values : undefined
                  };
                })}
                series={
                  grouped
                    ? chart.series.map((series, index) => ({
                        name: series.name,
                        color: seriesColor(index)
                      }))
                    : undefined
                }
                color={seriesColor(1)}
                width={width}
                height={PLOT_HEIGHT}
                tickAngle={0}
                xAxisHeight={22}
                formatValue={format}
                formatAxisValue={format}
                ariaLabel={title ?? undefined}
              />
            </div>
          </IntelligenceThemeScope>
        );
      }
      default:
        return <SupportChartTable chart={chart} title={title} />;
    }
  };

  return (
    <Slot ref={ref}>
      {title ? <Caption>{title}</Caption> : null}
      {render()}
    </Slot>
  );
};

export default SupportChart;
